import { Injectable, NgZone } from '@angular/core';
import {
  combineLatest,
  distinctUntilChanged,
  Subscription,
  tap,
  auditTime,
  filter,
  map,
  switchMap,
  EMPTY,
  BehaviorSubject,
} from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { MainToolbarSnackbarComponent } from '../ui/main-toolbar-snackbar/main-toolbar-snackbar.component';
import { SpaceRepository } from '../state/space.repository';
import { FrameEventType } from '../models/session-sync';
import { getTruncatedName } from '../common/utils/common-util';
import { DomListener } from '../utilities/DomListener';
import { MainToolbarService } from '../sessions/session/wb-main-toolbar-v2/services/main-toolbar.service';
import { RealtimeSpaceService } from './realtime-space.service';
import { SessionSharedDataService } from './session-shared-data.service';
import { SessionsVptService } from './sessions-vpt.service';
import { DomListenerFactoryService } from './dom-listener-factory.service';
import { UserService } from './user.service';
import { ChildModeService } from './child-mode.service';
import { ObserverModeService } from './observer-mode.service';
import { PanelLayoutService } from './panel-layout.service';
import { ScreenSizeService } from './screen-size.service';
import { DeviceAndBrowserDetectorService } from './device-and-browser-detector.service';

interface ViewportBox {
  left: number;
  top: number;
  width: number;
  height: number;
  color: string;
}

export enum ViewportState {
  SHOW = 'show',
  HIDE = 'hide',
}

const VIEWPORT_BOX_DEFAULT_COLOR = '#0082f5';
const VIEWPORT_SEND_THROTTLE_MS = 200;
const VIEWPORT_BOX_BORDER_SCREEN_PX = 2;
const VIEWPORT_LABEL_FONT_SIZE_SCREEN_PX = 14;
const VIEWPORT_LABEL_LINE_HEIGHT_SCREEN_PX = 19;
const VIEWPORT_LABEL_GAP_ABOVE_BOX_SCREEN_PX = 1;

@Injectable({
  providedIn: 'root',
})
export class UserViewportService {
  private viewportsContainerElement: HTMLElement | null = null;
  private subscriptions: Subscription = new Subscription();
  private viewportBoxElByUserId: Map<string, HTMLElement> = new Map();
  private viewportLabelElByUserId: Map<string, HTMLElement> = new Map();
  private lastViewportBoxByUserId: Map<string, ViewportBox> = new Map();
  private topBarHeightPxCache: number | null = null;
  private domListener: DomListener;

  private _currentViewportState = new BehaviorSubject<ViewportState>(ViewportState.HIDE);
  public currentViewportState$ = this._currentViewportState.asObservable();

  constructor(
    private realtimeSpaceService: RealtimeSpaceService,
    private spaceRepo: SpaceRepository,
    private sessionSharedDataService: SessionSharedDataService,
    private sessionsVptService: SessionsVptService,
    private userService: UserService,
    private translateService: TranslateService,
    private snackBar: MatSnackBar,
    private ngZone: NgZone,
    private domListenerFactoryService: DomListenerFactoryService,
    private mainToolbarService: MainToolbarService,
    private childModeService: ChildModeService,
    private observerModeService: ObserverModeService,
    private panelLayoutService: PanelLayoutService,
    private screenSizeService: ScreenSizeService,
    private deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
  ) {
    this.domListener = this.domListenerFactoryService.createInstance();
  }

  public init() {
    if (!this.sessionSharedDataService.fabricCanvas) {
      return;
    }
    this.viewportsContainerElement = this.getViewportsContainer();
    this.cleanup();

    // Send viewport event when the viewport transform changes and user is active
    const sendViewportEventSub = combineLatest([
      this.sessionsVptService.viewportTransform.pipe(
        filter((vpt): vpt is number[] => !!vpt),
        map((vpt) => [...vpt]),
        distinctUntilChanged((a, b) => this.areArraysEqual(a, b)),
        tap((vpt) => {
          this.changeViewportBoxesTransform(vpt);
        }),
        auditTime(VIEWPORT_SEND_THROTTLE_MS),
      ),
      this.mainToolbarService.canvasMouseMoveBuffered$,
      this.childModeService.isChildModeActive$,
      this.screenSizeService.currentSpaceCanvasWidth$, // Re-broadcast when canvas width changes
    ]).subscribe(() => {
      this.sendViewportEvent();
    });

    // Receive realtime viewport updates and render boxes
    const receiveViewportEventsSub = this.spaceRepo.isCurrentUserHost$
      .pipe(
        tap((isHost) => {
          if (!isHost) {
            // to clear viewport boxes if user demoted to participant
            this.clearViewportBoxes();
          }
        }),
        switchMap((isHost) => (isHost ? this.realtimeSpaceService.service.viewportEvent$ : EMPTY)),
        distinctUntilChanged(),
      )
      .subscribe(({ user, location, userName }) => {
        if (location.frameUID !== this.spaceRepo.activeSpaceSelectedBoardUid) {
          return;
        }
        const [canvasWidth, canvasHeight] = location.canvasSize;
        const rect = this.computeViewportRect(location.vpt, canvasWidth, canvasHeight);
        const color =
          this.sessionSharedDataService.usersPointerData.get(user) || VIEWPORT_BOX_DEFAULT_COLOR;
        this.upsertViewportHtml(user, userName || this.translateService.instant('User'), {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
          color,
        });
      });

    // Update color of viewport boxes when we learn a user's pointer color
    const colorEventsSub = this.spaceRepo.isCurrentUserHost$
      .pipe(
        switchMap((isHost) =>
          isHost ? this.sessionSharedDataService.userPointerDataEvent : EMPTY,
        ),
        distinctUntilChanged(),
      )
      .subscribe((data) => {
        if (!data) {
          return;
        }
        this.updateViewportColor(data.user, data.pointerColor);
      });

    // add subscriptions
    this.subscriptions.add(sendViewportEventSub);
    this.subscriptions.add(receiveViewportEventsSub);
    this.subscriptions.add(colorEventsSub);

    // hide viewport boxes by default and don't show snackbar
    this.toggleViewportBoxes(this._currentViewportState.getValue(), false);
  }

  private areArraysEqual(a: number[], b: number[]): boolean {
    if (a === b) {
      return true;
    }
    if (!a || !b || a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) {
        return false;
      }
    }
    return true;
  }

  private sendViewportEvent() {
    if (this.observerModeService.isObserverModeActive()) {
      return;
    }

    const vpt = this.sessionsVptService.viewportTransform.getValue();
    if (!vpt || !this.sessionSharedDataService.fabricCanvas) {
      return;
    }
    const [rawCanvasWidth, rawCanvasHeight] =
      this.sessionSharedDataService.fabricCanvas.getCanvasSize();
    const canvasWidth = this.panelLayoutService.getVisibleCanvasWidth(rawCanvasWidth);
    const canvasHeight = this.childModeService.isChildModeActive
      ? rawCanvasHeight
      : rawCanvasHeight - this.getTopBarHeightPx();
    const location = {
      vpt,
      canvasSize: [canvasWidth, canvasHeight] as [number, number],
      frameUID: this.sessionSharedDataService.getCurrentFrame()?.uid || '',
    };
    // Broadcast as a realtime frame event (viewport)
    this.realtimeSpaceService.service.sendFrameEvent(
      {
        type: FrameEventType.Viewport,
        location,
        userName: getTruncatedName(
          this.userService.user.getValue()?.user?.name ||
            this.userService.user.getValue()?.user?.email ||
            this.translateService.instant('User'),
          30,
        ),
      },
      true,
    );
  }

  private computeViewportRect(vpt: number[], canvasWidth: number, canvasHeight: number) {
    const scaleX = vpt[0] || 1;
    const scaleY = vpt[3] || 1;
    const translateX = vpt[4] || 0;
    const translateY = vpt[5] || 0;

    const left = -translateX / scaleX;
    const top = -translateY / scaleY;
    const width = canvasWidth / scaleX;
    const height = canvasHeight / scaleY;
    return { left, top, width, height };
  }

  private getTopBarHeightPx(): number {
    if (this.topBarHeightPxCache !== null) {
      return this.topBarHeightPxCache;
    }
    // get top bar height to subtract from canvas height as the top bar covers the top of the canvas
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--top-bar-v2-height')
      .trim();
    this.topBarHeightPxCache = raw ? parseFloat(raw) : 0;
    return this.topBarHeightPxCache;
  }

  private upsertViewportHtml(userId: string, userName: string, viewportBox: ViewportBox): void {
    const { left, top, width, height, color } = viewportBox;
    const container = this.viewportsContainerElement;
    if (!container) {
      return;
    }
    // Skip updates when viewport box is unchanged
    const lastViewportBox = this.lastViewportBoxByUserId.get(userId);
    if (
      lastViewportBox &&
      this.isSameViewportBox(lastViewportBox, { left, top, width, height, color })
    ) {
      return;
    }
    const id = this.getBoxElIdByUserId(userId);
    let box = this.getViewportBoxElByUserId(userId);
    if (!box) {
      box = document.createElement('div');
      box.id = id;
      box.className = 'viewport-box';
      box.setAttribute('data-user', userId);
      // Create label
      const label = document.createElement('div');
      label.className = 'viewport-label';
      label.id = this.getLabelElIdByUserId(userId);
      label.textContent = `${userName}`;
      box.appendChild(label);

      container.appendChild(box);
      this.viewportBoxElByUserId.set(userId, box);
    }
    box.style.left = `${left}px`;
    box.style.top = `${top}px`;
    box.style.width = `${width}px`;
    box.style.height = `${height}px`;
    box.style.backgroundColor = 'transparent';
    box.style.position = 'absolute';
    box.style.display = width > 1 && height > 1 ? 'block' : 'none';

    const labelEl =
      (box.querySelector('.viewport-label') as HTMLElement | null) ||
      (document.getElementById(this.getLabelElIdByUserId(userId)) as HTMLElement | null);
    if (labelEl) {
      this.viewportLabelElByUserId.set(userId, labelEl);
      labelEl.textContent = `${userName}`;
    }
    this.applyViewportBoxScreenSpaceStyles(userId, box, color, this.getLocalZoomScale());
    this.lastViewportBoxByUserId.set(userId, { left, top, width, height, color });
  }

  private getLocalZoomScale(): number {
    const vpt = this.sessionsVptService.viewportTransform.getValue();
    return vpt?.[0] || 1;
  }

  /**
   * Border/label sizes are set in canvas space as screenPx / zoom so they stay
   * constant on screen after the container's viewport matrix is applied.
   */
  private applyViewportBoxScreenSpaceStyles(
    userId: string,
    box: HTMLElement,
    color: string,
    zoomScale: number,
  ): void {
    const invZoom = 1 / zoomScale;
    const borderWidthCanvasPx = Math.max(
      VIEWPORT_BOX_BORDER_SCREEN_PX,
      VIEWPORT_BOX_BORDER_SCREEN_PX * invZoom,
    );
    box.style.borderColor = color;
    box.style.borderStyle = 'dashed';
    box.style.borderWidth = `${borderWidthCanvasPx}px`;

    const labelEl = this.viewportLabelElByUserId.get(userId);
    if (!labelEl) {
      return;
    }
    const fontSizeCanvasPx = Math.max(
      VIEWPORT_LABEL_FONT_SIZE_SCREEN_PX,
      VIEWPORT_LABEL_FONT_SIZE_SCREEN_PX * invZoom,
    );
    const lineHeightCanvasPx =
      fontSizeCanvasPx *
      (VIEWPORT_LABEL_LINE_HEIGHT_SCREEN_PX / VIEWPORT_LABEL_FONT_SIZE_SCREEN_PX);
    const labelTopOffsetCanvasPx = -(
      lineHeightCanvasPx +
      VIEWPORT_LABEL_GAP_ABOVE_BOX_SCREEN_PX * invZoom
    );

    labelEl.style.position = 'absolute';
    labelEl.style.top = `${labelTopOffsetCanvasPx}px`;
    labelEl.style.left = `-${borderWidthCanvasPx}px`; // to align the label with the box left border
    labelEl.style.fontFamily = 'Source Sans Pro, sans-serif';
    labelEl.style.fontSize = `${fontSizeCanvasPx}px`;
    labelEl.style.lineHeight = `${lineHeightCanvasPx}px`;
    labelEl.style.color = color;
    labelEl.style.whiteSpace = 'nowrap'; // to show the label in a single line
  }

  private updateAllViewportBoxesScreenSpaceStyles(zoomScale: number): void {
    this.viewportBoxElByUserId.forEach((box, userId) => {
      const color =
        this.lastViewportBoxByUserId.get(userId)?.color ||
        box.style.borderColor ||
        VIEWPORT_BOX_DEFAULT_COLOR;
      this.applyViewportBoxScreenSpaceStyles(userId, box, color, zoomScale);
    });
  }

  private updateViewportColor(userId: string, color: string) {
    const el = this.getViewportBoxElByUserId(userId);
    if (el) {
      (el as HTMLElement).style.borderColor = color;
    }
    const label =
      (el as HTMLElement | null)?.querySelector('.viewport-label') ||
      document.getElementById(this.getLabelElIdByUserId(userId));
    if (label) {
      (label as HTMLElement).style.color = color;
    }
    const last = this.lastViewportBoxByUserId.get(userId);
    if (last) {
      this.lastViewportBoxByUserId.set(userId, { ...last, color });
    }
  }

  private isSameViewportBox(viewportBox1: ViewportBox, viewportBox2: ViewportBox) {
    return (
      !!viewportBox1 &&
      !!viewportBox2 &&
      viewportBox1.left === viewportBox2.left &&
      viewportBox1.top === viewportBox2.top &&
      viewportBox1.width === viewportBox2.width &&
      viewportBox1.height === viewportBox2.height &&
      viewportBox1.color === viewportBox2.color
    );
  }

  private getViewportBoxElByUserId(userId: string): HTMLElement | null {
    const cached = this.viewportBoxElByUserId.get(userId);
    if (cached) {
      return cached;
    }
    const el = document.getElementById(this.getBoxElIdByUserId(userId)) as HTMLElement | null;
    if (el) {
      this.viewportBoxElByUserId.set(userId, el);
    }
    return el;
  }

  public removeViewportBoxForUser(userId: string) {
    const box = this.viewportBoxElByUserId.get(userId);
    if (box && box.parentElement) {
      box.parentElement.removeChild(box);
    }
    this.viewportBoxElByUserId.delete(userId);
    this.lastViewportBoxByUserId.delete(userId);
  }

  private clearViewportBoxes() {
    this.viewportBoxElByUserId.forEach((box) => {
      if (box.parentElement) {
        box.parentElement.removeChild(box);
      }
    });
    this.viewportBoxElByUserId.clear();
    this.lastViewportBoxByUserId.clear();
  }

  // set the viewport boxes to given state
  // if state is not provided, toggle the current state
  public toggleViewportBoxes(state?: ViewportState, showSnackbar = true) {
    // always hide viewport boxes on mobile devices as there is no entry point for showing/hiding them
    const isMobile = this.deviceAndBrowserDetectorService.isMobile();
    if (isMobile) {
      state = ViewportState.HIDE;
      showSnackbar = false;
    } else if (!state) {
      state =
        this._currentViewportState.getValue() === ViewportState.SHOW
          ? ViewportState.HIDE
          : ViewportState.SHOW;
    }
    this._currentViewportState.next(state);
    if (showSnackbar) {
      const message =
        state === ViewportState.SHOW
          ? this.translateService.instant('Viewports visible')
          : this.translateService.instant('Viewports hidden');
      this.openViewportSnackBar(message);
    }

    if (!this.viewportsContainerElement) {
      return;
    }
    this.viewportsContainerElement.style.display = state === ViewportState.SHOW ? 'block' : 'none';
  }

  private getViewportsContainer(): HTMLElement | null {
    return document.getElementById('overlay-canvas-viewports');
  }

  private changeViewportBoxesTransform(vpt: number[] | null) {
    if (!this.viewportsContainerElement || !vpt) {
      return;
    }
    this.viewportsContainerElement.style.transform = `matrix(${vpt.toString()})`;
    // Re-apply inverse-scaled stroke/label sizes so they stay constant on screen.
    this.updateAllViewportBoxesScreenSpaceStyles(vpt[0] || 1);
  }

  private getBoxElIdByUserId(userId: string): string {
    return `${userId}-viewport-box`;
  }

  private getLabelElIdByUserId(userId: string): string {
    return `${userId}-viewport-label`;
  }

  public cleanup() {
    this.subscriptions.unsubscribe(); // unsubscribe all active subscriptions
    this.subscriptions = new Subscription();
    // Clear viewport boxes and reset container styles
    const container = this.viewportsContainerElement;
    if (container) {
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
      container.style.transform = '';
      container.style.display = '';
    }
    this.domListener.clear();
    this.viewportBoxElByUserId.clear();
    this.lastViewportBoxByUserId.clear();
  }

  private openViewportSnackBar(message: string) {
    this.ngZone.runOutsideAngular(() => {
      if (this.snackBar._openedSnackBarRef) {
        this.snackBar.dismiss();
        this.snackBar._openedSnackBarRef.afterDismissed();
      }
      this.snackBar
        .openFromComponent(MainToolbarSnackbarComponent, {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: 'main-toolbar-snack-bar',
          data: message,
        })
        .afterDismissed();
    });
  }

  public isViewportBoxesVisible(): boolean {
    return this._currentViewportState.getValue() === ViewportState.SHOW;
  }
}
